/**
 * Main Application Entry Point
 *
 * Initializes the Vue application with all necessary plugins,
 * stores, and configuration.
 */
import { createApp } from 'vue';
import { initializeLogging, initializeTelemetry, createLogger, toTitleCase } from '@tms-phoenix/ui-vue';
import App from './App.vue';
import { router } from './router/instance';
import { routes } from './router';
import { authGuard, roleGuard, logoutGuard, entitlementsGuard, settingsOnboardingGuard, bridgelinkGuard } from './router/guards';
import { setupPinia, setupVueQuery, setupI18n } from './core/plugins';
import { config } from './core/config';
import { useUIStore, useAuthStore } from './stores';
import { vEntitlement } from './shared/directives/entitlement.directive';
import { hydrateMrnLabel } from './shared/composables/useMrnLabel';

// Import Tailwind CSS
import './styles/main.css';


// PrimeVue v4 config
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

// Pick a theme preset from @primevue/themes (Aura, Lara, Nora, etc.)
import Aura from '@primeuix/themes/aura';


// Initialize logging first
initializeLogging(config.logging as Parameters<typeof initializeLogging>[0]);

// Initialize telemetry
initializeTelemetry(config.telemetry);

const logger = createLogger('main');
logger.info('Initializing application', {
  mode: import.meta.env.MODE,
  version: config.app.version,
});

// Create Vue app instance
const app = createApp(App);

// Setup core plugins
setupPinia(app);
setupVueQuery(app);

// Re-schedule proactive token refresh after page reload.
// The persistence plugin restores tokens via $patch but does not trigger
// setAuth(), so we explicitly kick the timer here.

useAuthStore().initRefreshTimer();

// Setup i18n (i18next + i18next-vue) — loads public bundle with fallback resources.
// Uses L2 (localStorage) cache for instant init when available;
// falls back to blocking HTTP fetch only on first-ever visit.
await setupI18n(app);

// Apply the tenant's MRN term (e.g. "EKB") before anything mounts, so a reload
// of an authenticated session does not revert every MRN label to "MRN".
// Must run after setupI18n — it reads the bundle for the per-language default.
hydrateMrnLabel();

// Setup PrimeVue


app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: false,
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue'
      }
    }
  }
});
app.use(ToastService);

// Register global directives
app.directive('entitlement', vEntitlement);
app.directive('tooltip', Tooltip);
app.config.globalProperties.$titleCase = toTitleCase;

// Initialize UI store after Pinia is set up
const uiStore = useUIStore();
uiStore.initializeFromViewport();

// Setup router — add routes (kept separate from instance to avoid circular deps).
// Remove any pre-registered stub with the same name (e.g. the bootstrap /login
// route in instance.ts) so that the authoritative definition from authRoutes
// always takes effect, including its meta, guards, and layout config.
routes.forEach((route) => {
  if (route.name && router.hasRoute(route.name)) {
    router.removeRoute(route.name);
  }
  router.addRoute(route);
});

// Apply global guards
// Captured here because vue-router@4 router.onError only receives the error argument.
let pendingNavPath = location.pathname;
router.beforeEach(async (to, from, next) => {
  pendingNavPath = to.fullPath;
  logger.debug('Navigation start', { from: from.path, to: to.path });
  await authGuard(to, from, next);
});
router.beforeEach(roleGuard);
router.beforeEach(entitlementsGuard);
router.beforeEach(logoutGuard);
router.beforeEach(settingsOnboardingGuard);
router.beforeEach(bridgelinkGuard);


router.afterEach((to, from) => {
  logger.debug('Navigation complete', { from: from.path, to: to.path });
  // Track last authorized route for users with partial access
  // This allows redirecting them back to their last accessible route
  // when they try to access an unauthorized route
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  if (requiresAuth && to.path !== '/no-access' && to.path !== '/logout') {
    uiStore.setLastAuthorizedRoute(to.path);
  }
});

router.onError((error) => {
  logger.error('Router error', { error });

  // "Failed to fetch dynamically imported module" / ERR_ABORTED
  // Cause: browser cached old chunk hashes; server now serves new ones after
  // a redeployment. Navigate to the intended destination with fresh chunks.
  // Guard with sessionStorage to avoid an infinite reload loop.
  const isChunkLoadError =
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('dynamically imported module');

  if (isChunkLoadError) {
    const reloadKey = `chunk-reload:${pendingNavPath}`;
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1');
      window.location.href = pendingNavPath;
    }
  }
});

// Vite-native preload-error event (Vite ≥ 4.4).
// Fires before the router error for <link rel="modulepreload"> failures.
window.addEventListener('vite:preloadError', () => {
  const reloadKey = `vite-preload-reload:${location.pathname}`;
  if (!sessionStorage.getItem(reloadKey)) {
    sessionStorage.setItem(reloadKey, '1');
    window.location.reload();
  }
});

app.use(router);

// Mount application
app.mount('#app');

logger.info('Application mounted successfully');
